import { ProductRewardSearchResult } from "@brandclub/common-ui";
import _get from "lodash/get";
import _uniqBy from "lodash/uniqBy";
import { useCallback, useEffect, useState } from "react";
import { getBrandSpotlight } from "../../../../../api/graphQl/authenticated/SpotlightBrand";
import { useApolloClient } from "@apollo/client";

interface ISpotlightBrand {
  name: string;
  imageUrl: string;
  brandclubId: string;
  brandId: number;
}
const RESKEY = "BrandSpotlight";

export const useFetchSpotlightBrandProducts = ({
  pageSize = 25,
  autoInitFetch = true,
}: {
  pageSize?: number;
  autoInitFetch?: boolean;
}) => {
  const apolloClient = useApolloClient();
  const [spotlightBrand, setSpotlightBrand] = useState<ISpotlightBrand>();
  const [data, setData] = useState<ProductRewardSearchResult[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(
    async (pageNumber = 0) => {
      try {
        setError(null);

        const res = await apolloClient.query<{
          [RESKEY]: any;
        }>({
          query: getBrandSpotlight,
          variables: {
            page: {
              start: pageNumber,
              size: pageSize,
            },
          },
          fetchPolicy: "network-only",
        });

        const newData = _get(res, ["data", RESKEY, "trendingProducts"], []);
        const brand = _get(res, ["data", RESKEY], {});

        const newHasMore = _get(res, ["data", RESKEY, "hasMore"], false);
        setData((d) => _uniqBy([...d, ...newData], "stacklineSku"));
        setSpotlightBrand({ ...brand, trendingProducts: undefined });
        setHasMore(newHasMore);
      } catch (e) {
        if (e instanceof Error) {
          setError(e.message);
        }
        console.error(e);
      }
    },
    [pageSize, apolloClient]
  );

  const fetchInit = useCallback(async () => {
    try {
      setLoading(true);
      await fetchData(0);
    } finally {
      setPage(0);
      setLoading(false);
    }
  }, [fetchData]);

  const fetchMore = useCallback(async () => {
    try {
      setLoadingMore(true);
      await fetchData((page + 1) * pageSize);
    } finally {
      setPage((p) => p + 1);
      setLoadingMore(false);
    }
  }, [fetchData, page, pageSize]);

  useEffect(() => {
    if (autoInitFetch) {
      fetchInit();
    }
  }, [autoInitFetch, fetchInit]);

  // cut the data to fit the page size
  const resData = data.slice(0, (page + 1) * pageSize);

  return {
    data: resData,
    loading,
    error,
    hasMore,
    fetchMore,
    loadingMore,
    spotlightBrand,
    fetchInit,
  };
};
